"use client";

import { useEffect, useRef } from "react";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import type { Coordinates } from "./dispatch-client";

type MapViewProps = {
  you: Coordinates | null;
  mistri: Coordinates | null;
  height?: number;
  youLabel?: string;
  mistriLabel?: string;
};

function pinIcon(label: string, background: string) {
  return L.divIcon({
    className: "",
    html: `<div style="background:${background};color:#fff;font:800 10px Arial;padding:5px 8px;border-radius:10px;border:2px solid #fff;box-shadow:0 2px 8px rgba(0,0,0,.35);white-space:nowrap;transform:translate(-50%,-100%)">${label}</div>`,
    iconSize: [0, 0],
  });
}

export default function MapView({ you, mistri, height = 240, youLabel = "YOU", mistriLabel = "MISTRI" }: MapViewProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<L.Map | null>(null);
  const youMarkerRef = useRef<L.Marker | null>(null);
  const mistriMarkerRef = useRef<L.Marker | null>(null);
  const lineRef = useRef<L.Polyline | null>(null);

  useEffect(() => {
    if (!containerRef.current || mapRef.current) return;
    const map = L.map(containerRef.current, { zoomControl: true, attributionControl: false });
    L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", { maxZoom: 19 }).addTo(map);
    map.setView([20.5937, 78.9629], 5);
    mapRef.current = map;
    return () => {
      map.remove();
      mapRef.current = null;
      youMarkerRef.current = null;
      mistriMarkerRef.current = null;
      lineRef.current = null;
    };
  }, []);

  useEffect(() => {
    const map = mapRef.current;
    if (!map) return;

    if (you) {
      const position: [number, number] = [you.latitude, you.longitude];
      if (!youMarkerRef.current) {
        youMarkerRef.current = L.marker(position, { icon: pinIcon(youLabel, "#233b2b") }).addTo(map);
      } else {
        youMarkerRef.current.setLatLng(position);
      }
    }
    if (mistri) {
      const position: [number, number] = [mistri.latitude, mistri.longitude];
      if (!mistriMarkerRef.current) {
        mistriMarkerRef.current = L.marker(position, { icon: pinIcon(mistriLabel, "#b3552e") }).addTo(map);
      } else {
        mistriMarkerRef.current.setLatLng(position);
      }
    }
    if (you && mistri) {
      const path: [number, number][] = [[you.latitude, you.longitude], [mistri.latitude, mistri.longitude]];
      if (!lineRef.current) {
        lineRef.current = L.polyline(path, { color: "#3b5d46", weight: 3, dashArray: "6 8" }).addTo(map);
      } else {
        lineRef.current.setLatLngs(path);
      }
      map.fitBounds(L.latLngBounds(path), { padding: [40, 40], maxZoom: 16 });
    } else if (you) {
      map.setView([you.latitude, you.longitude], 15);
    } else if (mistri) {
      map.setView([mistri.latitude, mistri.longitude], 15);
    }
  }, [you, mistri, youLabel, mistriLabel]);

  return <div ref={containerRef} className="live-map" style={{ height }} aria-label="Live tracking map" />;
}
